/* eslint-disable max-len */
import { Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import { isEmpty, isEqual, size, xorWith } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { EStavLeiPlatby, ILeiDetailData, ILeiRejstrikResp } from 'types/leiTypes';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useProfily from 'hooks/useProfily';

import leiAktualizace, { leiAktualizaceInit } from 'services/lei/leiAktualizace';
import leiRejstrkApi from 'services/lei/leiRejstrik';

import { EPeriodType, ETypMatky, ETypZadostiLei } from 'enums/lei/leiDataEnums';
import { EEventType } from 'enums/lei/leiDetailDataEnums';
import { ELeiStavZadosti } from 'enums/lei/leiStavZadosti';
import { EMaterskaSpol } from 'enums/lei/materskaSpol';
import { ETypAdresy } from 'enums/lei/typAdresy';
import { emailRegExp, phoneNumberRegExp } from 'enums/regExp';

import getLanguageObject from 'utils/getLanguageObject';

import Osoba from './Osoba';
import Prehled from './Prehled';
import DetailInfoPrehled from './PrehledPages/DetailInfoPrehled';
import FinalniMatkaPrehled from './PrehledPages/FinalniMatkaPrehled';
import OsobaPrehled from './PrehledPages/OsobaPrehled';
import PrimaMatkaPrehled from './PrehledPages/PrimaMatkaPrehled';
import PrimaMatka from './PrimaMatka';
import Profondy from './Profondy';
import UpravaLeiDetail from './upravaLeiDetail';

interface INovyLei {}
export interface IRejstrikErrs {
    legalname: boolean;
    entitylegalformcode: boolean;
    legaladdress: boolean;
}

const NovyLei: React.FC<INovyLei> = () => {
    const [form] = Form.useForm();
    const { setLevel } = useProfily();
    const { language, lei, cisStaty, platbaLei } = useContext(Context);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [nextStepQueued, setNextStepQueued] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [showTitle, setShowTitle] = useState<boolean>(true);
    const [matkyErr, setMatkyErr] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [matkyFileListError, setMatkyFileListError] = useState<boolean>(false);
    const [rejstrikVals, setRejstrikVals] = useState<ILeiRejstrikResp>();
    const [rejstrikErrs, setRejstrikErrs] = useState<IRejstrikErrs>({
        legalname: false,
        entitylegalformcode: false,
        legaladdress: false
    });
    const [uploading, setUploading] = useState<boolean>(false);
    const formdata = lei?.detailData;
    const [formData, setFormData] = useState<ILeiDetailData | undefined>({
        ...formdata,
        // typzadosti: ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY,
        entitycheck: formdata?.entitycheck || 'N/A'
    });
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [PrehledOnly, setIsPrehledOnly] = useState<boolean>(
        lei?.stavZadosti === ELeiStavZadosti.HOTOVO || lei?.stavZadosti === ELeiStavZadosti.KONTROLA
    );

    const finalniMatkaPrehledData = formData?.matky?.filter(
        e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
    );
    const next = () => {
        form.resetFields();
        setCurrentStep(currentStep + 1);
        setIsEdit(false);
        window.scrollTo(0, 0);
    };

    const prev = () => {
        form.resetFields();
        setCurrentStep(currentStep - 1);
        window.scrollTo(0, 0);
    };

    const queueNextStep = () => {
        setNextStepQueued(true);
    };

    const backToPrehled = () => {
        form.resetFields();
        setIsEdit(false);
    };

    useEffect(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        if (!rejstrikVals && formData?.spolecnost && formData?.spolecnost?.registrationauthorityentityid) {
            setLoading(true);
            leiRejstrkApi({
                ic: formData?.spolecnost?.registrationauthorityentityid || '',
                stat: formData?.spolecnost.jurisdikce || '',
                lang: language,
                regauthor: formData.spolecnost.registrationauthorityid || ''
            })
                .then(resp => setRejstrikVals(resp))
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    useEffect(() => {
        const DataAddress = formData?.spolecnost?.adresy
            ? formData?.spolecnost?.adresy.filter(e => e.typadresy === ETypAdresy.LEGALADDRESS)[0]
            : {
                  firstaddressline: undefined,
                  city: undefined,
                  postalcode: undefined,
                  country: undefined
              };
        const RejstrikAddress = rejstrikVals?.adresy
            ? rejstrikVals.adresy.filter(e => e.typadresy === ETypAdresy.LEGALADDRESS)[0]
            : {
                  firstaddressline: undefined,
                  city: undefined,
                  postalcode: undefined,
                  country: undefined
              };
        const legalNameEvent =
            formData?.events && formData.events.length > 0
                ? formData.events.filter(e => e.eventtype === EEventType.CHANGE_LEGAL_NAME).length > 0
                : false;
        const legalFormEvent =
            formData?.events && formData.events.length > 0
                ? formData.events.filter(e => e.eventtype === EEventType.CHANGE_LEGAL_FORM).length > 0
                : false;
        const legalAddressEvent =
            formData?.events && formData.events.length > 0
                ? formData.events.filter(e => e.eventtype === EEventType.CHANGE_LEGAL_ADDRESS).length > 0
                : false;

        setRejstrikErrs({
            legalname:
                (formData?.spolecnost?.legalname || '').split(' ').join('') !==
                    (rejstrikVals?.legalname || '').split(' ').join('') &&
                !!rejstrikVals?.legalname &&
                !legalNameEvent,
            entitylegalformcode:
                (formData?.spolecnost?.entitylegalformcode || '').split(' ').join('') !==
                    (rejstrikVals?.entitylegalformcode || '').split(' ').join('') &&
                !!rejstrikVals?.entitylegalformcode &&
                !legalFormEvent,
            legaladdress:
                ((DataAddress.firstaddressline || '').split(' ').join('') !==
                    (RejstrikAddress.firstaddressline || '').split(' ').join('') &&
                    !!RejstrikAddress.firstaddressline) ||
                ((DataAddress.city || '').split(' ').join('') !== (RejstrikAddress?.city || '').split(' ').join('') &&
                    !!RejstrikAddress.city) ||
                ((DataAddress.postalcode || '').split(' ').join('') !==
                    (RejstrikAddress?.postalcode || '').split(' ').join('') &&
                    !!RejstrikAddress.postalcode) ||
                ((DataAddress.country || '').split(' ').join('') !==
                    (RejstrikAddress?.country || '').split(' ').join('') &&
                    !!RejstrikAddress.country &&
                    !legalAddressEvent)
        });
    }, [rejstrikVals, formData]);

    const primaMatkaData = formData?.matky?.filter(
        e => e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY || e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
    );
    const internationalMatkaData = formData?.matky?.filter(e => e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF);

    const isPrimaFinalniMatkaFileArraySame = function (x: string[], y: string[]) {
        const isSameSize = size(x) === size(y);
        return isSameSize && isEmpty(xorWith(x, y, isEqual));
    };
    const [areFilesEqual, setAreFilesEqual] = useState<boolean>(
        primaMatkaData?.length &&
            primaMatkaData[0].informacekmatce?.soubory &&
            finalniMatkaPrehledData?.length &&
            finalniMatkaPrehledData[0].informacekmatce?.soubory
            ? isPrimaFinalniMatkaFileArraySame(
                  primaMatkaData[0].informacekmatce?.soubory.map(e => e.orignazev),
                  finalniMatkaPrehledData[0].informacekmatce?.soubory.map(e => e.orignazev)
              )
            : false
    );

    const matkySubmit = (goNext?: boolean) => {
        const submit = () => {
            setMatkyErr(false);
            const fieldsValue = form.getFieldsValue();
            const primaMatkaInformace = formData?.matky
                ?.filter(
                    e =>
                        e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                        e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
                )
                .map(e => {
                    return {
                        informacekmatce: {
                            ...e.informacekmatce,
                            validationdocuments: fieldsValue.pvalidationdocuments,
                            ...(fieldsValue.pvalidationreference && {
                                validationreference: fieldsValue.pvalidationreference
                            }),
                            relationshipperiod: [
                                ...(fieldsValue.paccountingstart
                                    ? [
                                          {
                                              periodtype: EPeriodType.ACCOUNTING_PERIOD,
                                              ...(fieldsValue.paccountingstart && {
                                                  startdate: fieldsValue.paccountingstart.format('YYYY-MM-DD')
                                              }),
                                              ...(fieldsValue.paccountingend && {
                                                  enddate: fieldsValue.paccountingend.format('YYYY-MM-DD')
                                              })
                                          }
                                      ]
                                    : []),
                                ...(fieldsValue.pdocumentstart
                                    ? [
                                          {
                                              periodtype: EPeriodType.DOCUMENT_FILING_PERIOD,
                                              ...(fieldsValue.pdocumentstart && {
                                                  startdate: fieldsValue.pdocumentstart.format('YYYY-MM-DD')
                                              }),
                                              ...(fieldsValue.pdocumentend && {
                                                  enddate: fieldsValue.pdocumentend.format('YYYY-MM-DD')
                                              })
                                          }
                                      ]
                                    : []),
                                ...(fieldsValue.prelationship
                                    ? [
                                          {
                                              periodtype: EPeriodType.RELATIONSHIP_PERIOD,
                                              startdate: dayjs(fieldsValue?.prelationship).format('YYYY-MM-DD')
                                          }
                                      ]
                                    : [])
                            ]
                        }
                    };
                });
            const finalniMatkaInformace = formData?.matky
                ?.filter(e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY)
                .map(e => {
                    return {
                        informacekmatce: {
                            ...e.informacekmatce,
                            ...(((fieldsValue.fvalidationreference && !areFilesEqual) ||
                                (areFilesEqual && fieldsValue.pvalidationreference)) && {
                                validationdocuments: areFilesEqual
                                    ? fieldsValue.pvalidationreference
                                    : fieldsValue.fvalidationreference
                            }),
                            ...(((fieldsValue.fvalidationdocuments && !areFilesEqual) ||
                                (areFilesEqual && fieldsValue.pvalidationdocuments)) && {
                                validationdocuments: areFilesEqual
                                    ? fieldsValue.pvalidationdocuments
                                    : fieldsValue.fvalidationdocuments
                            }),
                            relationshipperiod: [
                                ...(fieldsValue.faccountingstart && !areFilesEqual
                                    ? [
                                          {
                                              periodtype: EPeriodType.ACCOUNTING_PERIOD,
                                              ...(fieldsValue.faccountingstart && {
                                                  startdate: fieldsValue.faccountingstart.format('YYYY-MM-DD')
                                              }),
                                              ...(fieldsValue.faccountingend && {
                                                  enddate: fieldsValue.faccountingend.format('YYYY-MM-DD')
                                              })
                                          }
                                      ]
                                    : areFilesEqual && fieldsValue.paccountingstart
                                      ? [
                                            {
                                                periodtype: EPeriodType.ACCOUNTING_PERIOD,
                                                ...(fieldsValue.paccountingstart && {
                                                    startdate: fieldsValue.paccountingstart.format('YYYY-MM-DD')
                                                }),
                                                ...(fieldsValue.paccountingend && {
                                                    enddate: fieldsValue.paccountingend.format('YYYY-MM-DD')
                                                })
                                            }
                                        ]
                                      : []),
                                ...(fieldsValue.fdocumentstart && !areFilesEqual
                                    ? [
                                          {
                                              periodtype: EPeriodType.DOCUMENT_FILING_PERIOD,
                                              ...(fieldsValue.fdocumentstart && {
                                                  startdate: fieldsValue.fdocumentstart.format('YYYY-MM-DD')
                                              }),
                                              ...(fieldsValue.fdocumentend && {
                                                  enddate: fieldsValue.fdocumentend.format('YYYY-MM-DD')
                                              })
                                          }
                                      ]
                                    : areFilesEqual && fieldsValue.pdocumentstart
                                      ? [
                                            {
                                                periodtype: EPeriodType.DOCUMENT_FILING_PERIOD,
                                                ...(fieldsValue.pdocumentstart && {
                                                    startdate: fieldsValue.pdocumentstart.format('YYYY-MM-DD')
                                                }),
                                                ...(fieldsValue.pdocumentend && {
                                                    enddate: fieldsValue.pdocumentend.format('YYYY-MM-DD')
                                                })
                                            }
                                        ]
                                      : []),
                                ...(finalniMatkaPrehledData &&
                                finalniMatkaPrehledData.length &&
                                fieldsValue.prelationship
                                    ? [
                                          {
                                              periodtype: EPeriodType.RELATIONSHIP_PERIOD,
                                              startdate: fieldsValue.prelationship.format('YYYY-MM-DD')
                                          }
                                      ]
                                    : [])
                            ]
                        }
                    };
                });
            const updatedMatky = formData?.matky?.map(e =>
                e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
                    ? {
                          ...e,
                          ...(e.informacekmatce && primaMatkaInformace?.length && primaMatkaInformace[0])
                      }
                    : e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                      ? e.stav === EMaterskaSpol.ANO_SAME
                          ? {
                                ...e,
                                ...(e.informacekmatce && primaMatkaInformace?.length && primaMatkaInformace[0])
                            }
                          : {
                                ...e,
                                ...(e.informacekmatce && finalniMatkaInformace?.length && finalniMatkaInformace[0])
                            }
                      : e
            );
            setFormData(pv => {
                return {
                    ...pv,
                    matky: updatedMatky
                };
            });
            goNext ? queueNextStep() : setIsEdit(true);
        };

        if (
            formData?.matky?.filter(
                e =>
                    e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                    e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
            ).length !== 2 &&
            formData?.typzadosti !== ETypZadostiLei.BRANCH &&
            goNext
        ) {
            setMatkyErr(true);
        } else {
            goNext ? form.validateFields().then(submit) : submit();
        }
    };

    const steps = [
        {
            title: 'LEI_AKTUALIZACE_INFORMACE_OPR',
            content: isEdit ? (
                <Osoba
                    prevStep={backToPrehled}
                    nextStep={queueNextStep}
                    setFormData={setFormData}
                    formDataOsoba={formData?.osoba}
                    formDataSpolecnost={formData?.spolecnost}
                    isEdit={isEdit}
                    setIsEdit={setIsEdit}
                />
            ) : (
                <OsobaPrehled
                    formDataOsoba={formData?.osoba}
                    isEdit={isEdit}
                    setIsEdit={setIsEdit}
                    nextStep={queueNextStep}
                    setFormData={setFormData}
                    formDataSpolecnost={formData?.spolecnost}
                />
            )
        },
        {
            title: 'LEI_DETAIL_INFO_TITLE_TABLE',
            content: (
                <>
                    <DetailInfoPrehled
                        formDataSpolecnost={formData?.spolecnost}
                        typZadosti={formData?.typzadosti}
                        rejstrikErrs={rejstrikErrs}
                    />
                    <UpravaLeiDetail
                        nextStep={next}
                        prevStep={prev}
                        setCurrentStep={setCurrentStep}
                        formData={formData}
                        setFormData={setFormData}
                        setIsEdit={setIsEdit}
                        cisStaty={cisStaty}
                        rejstrikVals={rejstrikVals}
                        rejstrikErrs={rejstrikErrs}
                    />
                </>
            )
        },
        {
            title: 'LEI_DETAIL_MAT_TITLE',
            content: isEdit ? (
                <PrimaMatka
                    nextStep={queueNextStep}
                    prevStep={backToPrehled}
                    setFormData={setFormData}
                    isEdit={isEdit}
                    cisStaty={cisStaty}
                    primaMatkaData={primaMatkaData}
                    internationalMatka={internationalMatkaData}
                    typZadosti={formData?.typzadosti}
                    finalniMatkaData={formData?.matky?.filter(
                        e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                    )}
                />
            ) : (
                <>
                    <PrimaMatkaPrehled
                        cisStaty={cisStaty}
                        upravaForm={form}
                        isEdit={isEdit}
                        setFormData={setFormData}
                        internationalMatkaData={internationalMatkaData}
                        //    getCountryText={getCountryText}
                        //  getRegionText={getRegionText}
                        primaMatkaData={primaMatkaData}
                        setUploading={setUploading}
                    />
                    {finalniMatkaPrehledData?.length ||
                    (primaMatkaData && primaMatkaData.length > 0 && primaMatkaData[0].stav === EMaterskaSpol.NE) ? (
                        <>
                            <Title
                                level={4}
                                text="LEI_FINAL_TITLE"
                                style={{
                                    marginTop: 10
                                }}
                            />
                            <FinalniMatkaPrehled
                                cisStaty={cisStaty}
                                disabled={primaMatkaData && primaMatkaData[0].stav === EMaterskaSpol.NE}
                                upravaForm={form}
                                isEdit={isEdit}
                                primaMatkaStav={
                                    (primaMatkaData && primaMatkaData.length && primaMatkaData[0].stav) || undefined
                                }
                                primaMatkaData={primaMatkaData}
                                setFormData={setFormData}
                                //  getCountryText={getCountryText}
                                //  getRegionText={getRegionText}
                                finalniMatkaData={formData?.matky?.filter(
                                    e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                                )}
                                areFilesEqual={areFilesEqual}
                                setAreFilesEqual={setAreFilesEqual}
                                setUploading={setUploading}
                            />
                        </>
                    ) : null}
                    {isEdit === false && (
                        <Row className="button-row">
                            <ButtonComponent
                                onClick={() => {
                                    prev();
                                    setMatkyFileListError(false);
                                    setMatkyErr(false);
                                }}
                                buttontext="ZPET"
                                type="default"
                                id="editZpetBtn"
                            />
                            <ButtonComponent
                                onClick={() => matkySubmit()}
                                buttontext="BUTTON_UPRAVIT"
                                type="primary"
                                id="matkySubmitBtn"
                            />
                            <ButtonComponent
                                type="primary"
                                htmlType="button"
                                loading={uploading}
                                buttontext={'POKRACOVAT'}
                                extramessage={
                                    matkyFileListError
                                        ? 'SOUBOR_NOT_LOADED'
                                        : matkyErr
                                          ? 'LEI_AKTUALIZACE_MATKA_ERR'
                                          : ''
                                }
                                onClick={() => matkySubmit(true)}
                                id="editPokracovatBtn"
                            />
                        </Row>
                    )}
                </>
            )
        },
        ...(formData?.typzadosti === 'FUND' || lei?.detailData?.typzadosti === 'FUND'
            ? [
                  {
                      title: 'LEI_FOND_TITLE',
                      content: (
                          <Profondy
                              nextStep={next}
                              prevStep={prev}
                              setFormData={setFormData}
                              vsechnyMatky={formData?.matky}
                          />
                      )
                  }
              ]
            : []),
        {
            title: 'PREHLED',
            content: (
                <Prehled
                    setShowTitle={setShowTitle}
                    PrehledOnly={PrehledOnly}
                    prevStep={prev}
                    setCurrentStep={setCurrentStep}
                    formData={formData}
                    isAktualizace={true}
                    idVarsymZadosti={
                        lei?.idVarsymZadosti || {
                            id: 0,
                            varsym: '0'
                        }
                    }
                />
            )
        }
    ];

    useEffect(() => {
        if (!lei?.idVarsymZadosti?.id) {
            setShowConfirmation(true);
            setCurrentStep(0);
        }
        if (PrehledOnly) return;
        if (
            lei?.idVarsymZadosti?.id &&
            currentStep === 0 &&
            formData?.osoba?.typosoby &&
            (!emailRegExp.test(formData?.osoba?.contactemail || '') ||
                !emailRegExp.test(formData?.osoba?.fakturacniemail || '') ||
                !phoneNumberRegExp.test(formData?.osoba?.contactphone || '') ||
                (formData?.osoba?.soubory?.length && typeof formData?.osoba?.omezenaplatnost !== 'boolean'))
        ) {
            setIsEdit(true);
        } else if (
            lei?.idVarsymZadosti?.id &&
            currentStep !== steps.length - 1 &&
            formData?.matky?.length &&
            !formData.platba?.stavplatby
        ) {
            setLoading(true);
            leiAktualizace(
                'put',
                lei?.idVarsymZadosti?.varsym ? lei.idVarsymZadosti.varsym : '0',
                lei?.idVarsymZadosti?.id ? lei.idVarsymZadosti.id : 0,
                {
                    ...formData,
                    language: language
                }
            )
                .then(() => {
                    if (nextStepQueued) {
                        next();
                        setNextStepQueued(false);
                    }
                })
                .finally(() => setLoading(false));
        } else {
            !formData?.platba?.stavplatby && !formData?.stavleiplatba && setShowConfirmation(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, lei?.detailData]);

    useEffect(() => {
        if (PrehledOnly) setCurrentStep(steps.length - 1);
        if (platbaLei) setCurrentStep(steps.length - 1);
        if (
            lei?.detailData?.platba?.stavplatby &&
            (lei.detailData.platba.stavplatby === EStavLeiPlatby.PLODLO ||
                lei.detailData.platba.stavplatby === EStavLeiPlatby.CEKA ||
                lei.detailData.platba.stavplatby === EStavLeiPlatby.VYPR)
        )
            setCurrentStep(steps.length - 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => setLevel('platbaLei', null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LoadingWrapper loading={loading}>
            <Title
                level={3}
                text={
                    currentStep > 0
                        ? currentStep === steps.length - 1 && PrehledOnly
                            ? `${getLanguageObject(language).LEI_PREHLED_ODESLANO_AKTUALIZACE_TITLE} ${
                                  formData?.subcategory
                                      ? '– ' + getLanguageObject(language)[formData?.subcategory]
                                      : '– ' + getLanguageObject(language)['LEI_' + formData?.typzadosti]
                              } ${lei?.datumPodani ? '– ' + dayjs(lei?.datumPodani).format('DD.MM.YYYY') : ''}`
                            : `${getLanguageObject(language).LEI_AKTUALIZACE_TITLE} - ${
                                  getLanguageObject(language)['LEI_' + formData?.typzadosti]
                              } ${
                                  formData?.typzadosti === ETypZadostiLei.RESIDENT_GOVERNMENT_ENTITY &&
                                  formData.subcategory
                                      ? '(' + getLanguageObject(language)[formData.subcategory] + ')'
                                      : ''
                              }`
                        : getLanguageObject(language).LEI_AKTUALIZACE_TITLE
                }
            />
            {showConfirmation ? (
                <>
                    <TranslateText>LEI_UPDATE_CONFIRM</TranslateText>
                    <Row className="button-row">
                        <ButtonComponent
                            type="primary"
                            id="confirmationBtn"
                            htmlType="button"
                            buttontext={'ANO'}
                            onClick={() => {
                                setLoading(true);
                                leiAktualizaceInit(lei?.idVarsymZadosti?.varsym || '0')
                                    .then(resp => {
                                        leiAktualizace('get', resp.varsym, resp.id).then(data => {
                                            setLevel('lei', {
                                                ...lei,
                                                idVarsymZadosti: resp,
                                                detailData: data,
                                                data: undefined
                                            });
                                            setIsPrehledOnly(false);
                                            setFormData({
                                                ...data,
                                                entitycheck: data.entitycheck || 'N/A',
                                                osoba: {
                                                    ...data?.osoba,
                                                    soubory: (
                                                        data.osoba?.omezenaplatnost
                                                            ? dayjs(data.osoba.platnost) > dayjs()
                                                            : true
                                                    )
                                                        ? data.osoba?.soubory
                                                        : []
                                                },
                                                matky: data?.matky?.map(e => {
                                                    const matka = {
                                                        ...e
                                                    };
                                                    e.stav === EMaterskaSpol.ANO && delete matka.duvod;
                                                    return {
                                                        ...matka,
                                                        ...(e.informacekmatce && {
                                                            informacekmatce: {
                                                                datumodkdyplatnost:
                                                                    e.informacekmatce?.datumodkdyplatnost,
                                                                konsolidace: e.informacekmatce?.konsolidace,
                                                                podilspolecnosti: e.informacekmatce?.podilspolecnosti,
                                                                soubory:
                                                                    e.typmatky ===
                                                                        ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                                                                    e.typmatky ===
                                                                        ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY
                                                                        ? []
                                                                        : e.informacekmatce.soubory,
                                                                relationshipperiod:
                                                                    e.informacekmatce?.relationshipperiod?.filter(
                                                                        o =>
                                                                            o.periodtype ===
                                                                            EPeriodType.RELATIONSHIP_PERIOD
                                                                    )
                                                            }
                                                        })
                                                    };
                                                })
                                            });
                                            setShowConfirmation(false);
                                        });
                                    })
                                    .finally(() => setLoading(false));
                            }}
                        />
                    </Row>
                </>
            ) : (
                <>
                    {showTitle && <Title level={4} text={steps[currentStep].title} />}
                    <Row gutter={8}>
                        <Col span={24}>{steps[currentStep].content}</Col>
                    </Row>
                </>
            )}
        </LoadingWrapper>
    );
};

export default NovyLei;
